import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { adminAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { formatDate } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './Manage.css';

const ManageExperience = () => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ companyName: '', position: '', location: '', startDate: '', endDate: '', isCurrent: false, description: '', achievements: '', displayOrder: 0 });

  const loadData = async () => {
    try {
      const res = await adminAPI.getExperiences();
      if (res.success) setExperiences(res.data);
    } catch (err) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ companyName: '', position: '', location: '', startDate: '', endDate: '', isCurrent: false, description: '', achievements: '', displayOrder: 0 });
    setShowModal(true);
  };

  const openEdit = (exp) => {
    setEditing(exp);
    setForm({
      companyName: exp.companyName, position: exp.position, location: exp.location || '',
      startDate: exp.startDate?.split('T')[0] || '', endDate: exp.endDate?.split('T')[0] || '',
      isCurrent: exp.isCurrent, description: exp.description,
      achievements: exp.achievements?.join(', ') || '', displayOrder: exp.displayOrder,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await adminAPI.updateExperience(editing.id, form); toast.success('Updated'); }
      else { await adminAPI.createExperience(form); toast.success('Created'); }
      setShowModal(false); loadData();
    } catch (err) { toast.error('Operation failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this experience?')) return;
    try { await adminAPI.deleteExperience(id); toast.success('Deleted'); loadData(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="manage-page">
      <Helmet><title>Manage Experience | Portfolio CMS</title></Helmet>
      <div className="manage-header">
        <h2>Experience Management</h2>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus /> New Experience</button>
      </div>
      <div className="manage-table">
        <table>
          <thead><tr><th>Company</th><th>Position</th><th>Duration</th><th>Current</th><th>Actions</th></tr></thead>
          <tbody>
            {experiences.map(e => (
              <tr key={e.id}>
                <td><strong>{e.companyName}</strong></td>
                <td>{e.position}</td>
                <td>{formatDate(e.startDate)} - {e.isCurrent ? 'Present' : formatDate(e.endDate)}</td>
                <td>{e.isCurrent ? 'Yes' : 'No'}</td>
                <td><div className="action-btns">
                  <button className="btn-icon edit" onClick={() => openEdit(e)}><FiEdit2 /></button>
                  <button className="btn-icon delete" onClick={() => handleDelete(e.id)}><FiTrash2 /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Edit Experience' : 'New Experience'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group"><label>Company</label><input value={form.companyName} onChange={e => setForm({...form, companyName: e.target.value})} required /></div>
                <div className="form-group"><label>Position</label><input value={form.position} onChange={e => setForm({...form, position: e.target.value})} required /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Start Date</label><input type="date" value={form.startDate} onChange={e => setForm({...form, startDate: e.target.value})} required /></div>
                <div className="form-group"><label>End Date</label><input type="date" value={form.endDate} onChange={e => setForm({...form, endDate: e.target.value})} disabled={form.isCurrent} /></div>
              </div>
              <div className="form-group"><label><input type="checkbox" checked={form.isCurrent} onChange={e => setForm({...form, isCurrent: e.target.checked})} /> Currently working here</label></div>
              <div className="form-group"><label>Description</label><textarea rows="3" value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
              <div className="form-group"><label>Achievements (comma separated)</label><input value={form.achievements} onChange={e => setForm({...form, achievements: e.target.value})} /></div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageExperience;
